import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Clock, Plane, ListChecks, Calendar, TrendingUp, AlertTriangle } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, BarChart, Bar, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const StatCard = ({ icon: Icon, label, value, sub, tone = "blue", testId }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400",
    green: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    amber: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
    rose: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400",
  };
  return (
    <Card data-testid={testId} className="card-hover border-border/60">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg grid place-items-center ${tones[tone]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        <div className="overline mb-1">{label}</div>
        <div className="font-heading text-3xl font-extrabold tracking-tight">{value}</div>
        {sub && <div className="text-sm text-slate-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
};

const ClockWidget = ({ onChanged }) => {
  const { t } = useI18n();
  const [entry, setEntry] = useState(null);
  const [now, setNow] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(i); }, []);

  const load = async () => {
    try {
      const r = await api.get("/timeclock/current");
      setEntry(r.data);
    } catch (_e) {}
  };
  useEffect(() => { load(); }, []);

  const doAction = async (action) => {
    try {
      await api.post(`/timeclock/action?action=${action}`);
      toast.success("Erfasst");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const onBreak = entry?.breaks?.length && !entry.breaks[entry.breaks.length - 1].end;

  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg">Stempeluhr</CardTitle>
          <div className="text-3xl font-extrabold tabular-nums font-heading">
            {now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {entry ? (
          <div className="mb-4 flex items-center gap-2 text-sm">
            <div className={`h-2 w-2 rounded-full ${onBreak ? "bg-amber-500" : "bg-emerald-500"} animate-pulse`} />
            <span className="text-slate-600">
              {onBreak ? "In Pause" : "Eingestempelt"} seit {new Date(entry.clock_in).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        ) : (
          <div className="mb-4 text-sm text-slate-500">Noch nicht eingestempelt heute.</div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button data-testid="dash-clock-in" onClick={() => doAction("in")} disabled={!!entry} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
            {t("clock_in")}
          </Button>
          <Button data-testid="dash-clock-out" onClick={() => doAction("out")} disabled={!entry} className="rounded-full bg-rose-600 hover:bg-rose-700">
            {t("clock_out")}
          </Button>
          <Button data-testid="dash-break-start" onClick={() => doAction("break_start")} disabled={!entry || onBreak} variant="outline" className="rounded-full">
            {t("break_start")}
          </Button>
          <Button data-testid="dash-break-end" onClick={() => doAction("break_end")} disabled={!onBreak} variant="outline" className="rounded-full">
            {t("break_end")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/reports/dashboard"),
        api.get("/timeclock/mine?limit=14"),
      ]);
      setStats(r1.data);
      setEntries(r2.data);
      if (isAdmin) {
        const r3 = await api.get("/reports/admin");
        setAdminStats(r3.data);
      }
    } catch (_e) {}
  };
  useEffect(() => { load(); }, [isAdmin]);

  const chartData = entries
    .slice(0, 10)
    .reverse()
    .map((e) => ({
      date: e.date?.slice(5),
      hours: Number(((e.net_minutes || 0) / 60).toFixed(2)),
    }));

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="overline">Willkommen zurück</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
            Hallo, {user?.first_name} 👋
          </h2>
        </div>
        {isAdmin && adminStats?.over_buffer_count > 0 && (
          <button
            data-testid="dash-overtime-warn"
            onClick={() => navigate("/reports")}
            className="flex items-center gap-2 rounded-full bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 px-4 py-2 text-sm font-semibold hover:bg-amber-100 dark:hover:bg-amber-950/60"
          >
            <AlertTriangle className="w-4 h-4" />
            {adminStats.over_buffer_count} Überstunden ohne Begründung
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testId="stat-week-hours"
          icon={Clock}
          label={t("week_hours")}
          value={`${stats?.week_hours ?? 0}h`}
          sub={`Ziel: ${stats?.weekly_target ?? 40}h`}
          tone="blue"
        />
        <StatCard
          testId="stat-overtime"
          icon={TrendingUp}
          label={t("overtime")}
          value={`${stats?.overtime_hours ?? 0}h`}
          sub="Diesen Monat"
          tone="amber"
        />
        <StatCard
          testId="stat-vacation"
          icon={Plane}
          label={t("vacation")}
          value={`${stats?.vacation_remaining ?? 0}`}
          sub={`von ${stats?.vacation_total ?? 30} Tagen`}
          tone="green"
        />
        <StatCard
          testId="stat-tasks"
          icon={ListChecks}
          label={t("open_tasks")}
          value={stats?.open_tasks ?? 0}
          tone="rose"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card className="border-border/60 h-full">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">Arbeitsstunden — letzte 10 Tage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Bar dataKey="hours" fill="#1D4ED8" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
        <ClockWidget onChanged={load} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {t("next_shift")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.next_shift ? (
              <div className="space-y-2">
                <div className="font-heading text-2xl font-extrabold">
                  {stats.next_shift.date} · {stats.next_shift.start_time}–{stats.next_shift.end_time}
                </div>
                <div className="text-sm text-slate-500">
                  {stats.next_shift.area || "Bereich unbestimmt"} · {stats.next_shift.notes || ""}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">Keine geplanten Schichten.</div>
            )}
          </CardContent>
        </Card>

        {isAdmin && adminStats && (
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">Team-Übersicht</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Mitarbeiter aktiv</span><span className="font-semibold">{adminStats.total_users}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Standorte</span><span className="font-semibold">{adminStats.total_locations}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Offene Anträge</span><Badge variant="secondary">{adminStats.open_absences}</Badge></div>
              <div className="flex justify-between"><span className="text-slate-500">Krankheitstage (Monat)</span><span className="font-semibold">{adminStats.sick_days_month}</span></div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
